// src/layouts/dashboard/DashboardContainer.jsx
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment/moment";
import Swal from "sweetalert2";
import crowdCountingAPI from '../../../../api/crowd_counting';
import { useAuth0 } from "@auth0/auth0-react";
import HeadBodyFooterContainer from "../frames/HeadBodyFooterContainer";
import processCharts from "../../utils/processCharts";
import BaseLineChart from "../../components/charts/BaseLineChart";
import DonutChart from "../../components/charts/DonutChart";
import SoftBox from "components/SoftBox";
import { DonutChartWrapper } from "../../components/charts/DonutWrapper";
// import { Box, Button, Card, Divider, Grid, IconButton, Radio, Tab, Tabs } from "@mui/material";
import { Grid, Card, Box, Typography, Button, Tabs, Tab, Container } from '@mui/material';
import SoftTypography from "components/SoftTypography";
import { SoftUISelect } from "../../components/ui/SoftUISelect";
import SoftUIGroupTable from "../../components/tables/SoftUIGroupTable"
import stationConfig from "../../config/stationConfig"
import SoftPagination from "components/SoftPagination";
import { LineChartWrapper } from "../../components/charts/LineChartWrapper"
import CorridorArrowsMapOptimised from "../../components/map/CorridorArrowsMapOptimised"
import CorridorDotsMapOptimised from "../../components/map/CorridorDotsMapOptimised"

import stationGeoJSON from "../../config/stationGeoJSON"
import CustomTabPanel from "modules/crowdcounting/components/CustomTabPanel";
import ExampleStationKpis from "../../components/ui/SoftKPIBar";
import { SoftKPIHeroRow } from "../../components/ui/XLKPIRow";
import { SoftStatCardXL } from "../../components/ui/SoftStatCardXL";
import ControlledRadioButtonsGroup from "../../components/ui/ControlledRadioButtonsGroup";
import SoftButton from "components/SoftButton";
import SoftUIButtonWithDesc from "../../components/ui/SoftUIButtonWithDesc";
import RPiLatestImage from "../../components/ui/LatestRPiImage";
import { ArrowBack, Lock, LockOpen } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SoftUIGroupTimeTable from "../../components/tables/SoftUIGroupTimeTable";
import SoftUITimeTableShellFrozen from "../../components/tables/SoftUITimeTableShellFrozen";
import SoftUITimeTableShellVerticalFrozen from "../../components/tables/SoftUITimeTableShellVerticalFrozen";

let current_data;

function buildTreeStateFromDataTree(dataNode, defaultExpand = false) {
    const children = dataNode?.children || {};
    const stateChildren = {};

    for (const [key, childNode] of Object.entries(children)) {
        stateChildren[key] = buildTreeStateFromDataTree(childNode, defaultExpand);
    }


    return {
        expand: defaultExpand,
        visible: true,
        level: dataNode.group,
        children: stateChildren,
    };
}



// Safely toggle expand at a path like ["main","front"]
function toggleExpandAtPath(prevState, path) {
    if (!path.length) return { ...prevState, expand: !prevState.expand };

    const [head, ...rest] = path;
    const prevChild = prevState.children?.[head];

    // if path points to missing key, no-op
    if (!prevChild) return prevState;

    return {
        ...prevState,
        children: {
            ...prevState.children,
            [head]: toggleExpandAtPath(prevChild, rest),
        },
    };
}


/**
 * Parent container for StationDashboard.
 *
 * Inputs:
 *  - event: { start_time: epochSeconds, end_time: epochSeconds }  // may be in the future for live events
 *  - device_uuids: string[]
 *
 * Responsibilities:
 *  - Owns time bounds + current view window + hovered epoch
 *  - Computes startLimit/endLimit from event, but applies a "soft" endLimit when event end is in the future
 *  - Leaves a clear spot for your React Query data fetching
 *  - Builds the request payload you will send to server
 *
 * Server payload you requested:
 * {
 *   start_time: startTime,
 *   end_time: endTime,
 *   group_size: group_size,
 *   uuids: device_uuids,
 *   context_start_time: startLimit
 * }
 */

const SETTINGS = [
    {
        label: 'Marker Colours',
        value: 'breakdown',
        options: [
            {
                label: 'Groups',
                value: 'groups'
            },
            {
                label: 'Ingress / Egress',
                value: 'direction'
            }
        ]
    },
    {
        label: 'Movement type',
        value: 'movement',
        options: [
            {
                label: 'Rate',
                value: 'flow'
            },
            // {
            //     label: 'Count',
            //     value: 'count'
            // },
            {
                label: 'Total',
                value: 'accumulative'
            }
        ]
    },
    {
        label: 'Chart Stacking',
        value: 'stacking',
        options: [
            {
                label: 'Compare',
                value: false
            },
            {
                label: 'Stack',
                value: true
            }
        ]
    },
    {
        label: 'Direction',
        value: 'direction',
        options: [
            {
                label: 'Ingress',
                value: 'in'
            },
            {
                label: 'Egress',
                value: 'out'
            },
            {
                label: 'Both',
                value: 'both'
            },
            {
                label: 'Net',
                value: 'net'
            }
        ]
    },
    {
        label: 'Time Binning',
        value: 'binning',
        options: [
            {
                label: 'Auto',
                value: 'auto'
            },
            {
                label: '1h',
                value: 60 * 60
            },
            {
                label: '30m',
                value: 30 * 60
            },
            {
                label: '15m',
                value: 15 * 60
            },
            {
                label: '5m',
                value: 5 * 60
            }
        ]
    },
    {
        label: 'Time Binning',
        value: 'table_binning',
        options: [
            {
                label: '1h',
                value: 60 * 60
            },
            {
                label: '30m',
                value: 30 * 60
            },
            {
                label: '15m',
                value: 15 * 60
            }
        ]
    }
];

const INGRESS_EGRESS_COLOURS = {
    ingress: "#4FD1C5",
    egress: "#ed636a"
}


function corridorTemplate(row, geojson, group_size, key, colour) {
    group_size /= 3;

    return {
        id: key,
        colorA: colour.in, //"#4FD1C5", // A → B
        colorB: colour.out, //"#ed636a", // B → A
        // circles per second
        throughputA: (row.in / group_size),   // A -> B
        throughputB: (row.out / group_size),   // B -> A

        // NEW: explicit dot populations (steady-state)
        countA: Math.ceil(row.in / group_size),
        countB: Math.ceil(row.out / group_size),

        fillRgba: colour.in,
        fillOpacity: 0.0,

        leftBoundary: geojson.left,
        rightBoundary: geojson.right,

        // NEW: segment limits
        // A runs 10% -> 92%
        startPointA: 10,
        endPointA: 90,
        // B runs 15% -> 85% (but B travels from 85% down to 15%)
        startPointB: 30,
        endPointB: 70,
    }
}

// const myCorridors = [
//     {
//         id: "funnel-demo",
//         colorA: "#4FD1C5", // A → B
//         colorB: "#ed636a", // B → A
//         // circles per second
//         throughputA: 18,   // A -> B
//         throughputB: 10,   // B -> A

//         // congestion percentage (0..1 or 0..100)
//         congestionA: 0.6,  // 60% of max density/cap
//         congestionB: 0.3,

//         leftBoundary: {
//             type: "Feature",
//             geometry: {
//                 type: "LineString",
//                 coordinates: [
//                     [151.07040, -33.84610],
//                     [151.07005, -33.84635],
//                     [151.06975, -33.84660],
//                     [151.06950, -33.84685],
//                     [151.06930, -33.84710],
//                     [151.06910, -33.84735]
//                 ]
//             }
//         },
//         rightBoundary: {
//             type: "Feature",
//             geometry: {
//                 type: "LineString",
//                 coordinates: [
//                     [151.07105, -33.84625],
//                     [151.07055, -33.84648],
//                     [151.07010, -33.84670],
//                     [151.06972, -33.84692],
//                     [151.06965, -33.84715],
//                     [151.06975, -33.84745]
//                 ]
//             }
//         }
//     }
// ];



function walkKeyTreeColour(obj, nodeState, node, key, colour) {

    if (colour) {
        obj[key] = colour;
    }
    else {
        obj[key] = node.colour
    }

    if (nodeState.expand) {
        for (var child_key in nodeState.children) {
            obj = {
                ...obj,
                ...walkKeyTreeColour(obj, nodeState.children[child_key], node.children[child_key], child_key, colour)
            }
        }
    }
    else {
        for (var child_key in nodeState.children) {
            obj = {
                ...obj,
                ...walkKeyTreeColour(obj, nodeState.children[child_key], node.children[child_key], child_key, obj[key])
            }
        }
    }

    return obj;

}

let last_epoch_change = 0;


export default function DashboardContainer({
    event,
    device_uuids = []
}) {
    // -------------------------
    // Time state owned by parent
    // -------------------------

    // debugger;

    const navigate = useNavigate();

    const [startLimit, setStartLimit] = useState(null); // absolute lower bound (from event.start_time)
    const [endLimit, setEndLimit] = useState(null);     // absolute/soft upper bound (see below)
    const [startTime, setStartTime] = useState(null);   // current visible window start
    const [endTime, setEndTime] = useState(null);       // current visible window end
    const [epoch, setEpoch] = useState(null);           // hovered/inspected epoch
    const [highlightedTimePeriod, setHighlightedTimePeriod] = useState(null);           // hovered/inspected epoch
    const [lockedToLive, setLockedToLive] = useState(true);           // hovered/inspected epoch
    const [groupBy, setGroupBy] = useState('group');

    const [settingsTabValue, setSettingsTabValue] = useState(0);
    const [viewTabValue, setViewTabValue] = useState(0);

    const [settings, setSettings] = useState({
        'breakdown': 'groups',
        'movement': 'accumulative',
        'stacking': 'false',
        'direction': 'net',
        'binning': 'auto',
        'table_binning': 60 * 60
    })

    let hasLive = moment(event.end_time).isAfter(moment());

    if (settings.binning == 'auto') {
        var group_sizes = [60, 100, 150, 300];

        var group_size = Math.ceil((endTime - startTime) / 60);

        var smallest_group_size = group_sizes.filter(r => r < group_size).pop()

        if (!smallest_group_size) {
            group_size = 30;
        }
        else {
            group_size = smallest_group_size;
        }
    }
    else {
        // debugger;
        group_size = Number(settings.binning)
    }

    const crowdCounting = useQuery({

        queryKey: ['crowdCounting2', startTime, endTime, device_uuids, startLimit, group_size],
        staleTime: Infinity,
        enabled: !!startTime,
        placeholderData: (prev) => prev,
        keepPreviousData: true,
        queryFn: async () => {

            try {


                query.group_size = group_size;
                var data = await crowdCountingAPI.getAllData(query);

                var processed_data = processCharts(data.egress.graph, data.egress.context, data.metadata, data.metadata.group_size);
                var settings = SETTINGS.filter(r => r.value == 'table_binning').pop();

                processed_data.manual_binning = {};

                if (settings) {
                    for (var row of settings.options) {
                        processed_data.manual_binning[row.value] = processCharts(data.egress.graph, data.egress.context, data.metadata, row.value)
                    }
                }

                processed_data.metadata = data.metadata;
                return processed_data;

            }
            catch (e) {
                Swal.hideLoading(); // Hide the loader
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'An error occurred during processing.'
                });
                console.error(e)
            }
        }
    });


    const [treeState, setTreeState] = useState(() => ({
        all: {
            expand: false, // root open by default
            visible: true,
            level: 'all',
            children: buildTreeStateFromDataTree(stationConfig.tree, false).children,
        },
    }));

    const { user } = useAuth0();
    crowdCountingAPI.loadToken(useAuth0());

    // -------------------------
    // Compute limits from event
    // -------------------------
    useEffect(() => {
        const eventStart = Number(moment(event?.start_time).unix());
        const eventEnd = Number(moment(event?.end_time).unix());

        if (!Number.isFinite(eventStart) || !Number.isFinite(eventEnd)) {
            setStartLimit(null);
            setEndLimit(null);
            setStartTime(null);
            setEndTime(null);
            setEpoch(null);
            return;
        }

        // Hard start is always the event start
        const hardStartLimit = eventStart;

        // Soft end limit:
        // - If event end is in the past, endLimit = event end
        // - If event end is in the future (live), endLimit = "now-ish" but not beyond event end
        //
        // "now-ish" uses a small buffer so you can pan/zoom slightly ahead without the axis feeling stuck.
        const nowSec = Math.floor(Date.now() / 1000);
        const LIVE_LOOKAHEAD_SEC = 10; // 5 min lookahead buffer (tweakable)
        const softEndLimit = Math.min(eventEnd, Math.max(eventStart, nowSec + LIVE_LOOKAHEAD_SEC));

        setStartLimit(hardStartLimit);
        setEndLimit(softEndLimit);

        // Initialize/repair the visible window:
        setStartTime((prev) => {
            if (prev == null) return hardStartLimit;
            return Math.max(hardStartLimit, prev);
        });

        setEndTime((prev) => {
            if (prev == null) return softEndLimit;
            return Math.min(softEndLimit, prev);
        });

        // If epoch is now out of bounds, clear it
        setEpoch((prev) =>
            prev != null && (prev < hardStartLimit || prev > softEndLimit) ? null : prev
        );

    }, [event?.start_time, event?.end_time]);


    // Keep soft endLimit moving forward during live events
    useEffect(() => {
        const eventStart = moment(event?.start_time).unix();
        const eventEnd = moment(event?.end_time).unix();

        const LIVE_LOOKAHEAD_SEC = - 10;
        const TICK_MS = 10 * 1000; // update every 10s (tweakable)

        function goToLive() {

            const now = Math.floor(Date.now() / 1000);
            // debugger;
            const nextSoftEnd = Math.min(eventEnd, Math.max(eventStart, now));

            // debugger;

            setEndLimit((prev) => {
                // only move forward (avoid jitter)
                if (prev == null) return nextSoftEnd;
                return Math.max(prev, nextSoftEnd);
            });

            if (endTime == null) {
                setEndTime(nextSoftEnd);
            }
            else if (lockedToLive && hasLive) {

                if (startTime != startLimit) {
                    let range = endTime - startTime;
                    setStartTime(nextSoftEnd - range);
                }
                setEndTime(nextSoftEnd)

                if (crowdCounting.data && last_epoch_change < moment().unix() - 15) {
                    // console.log(Object.values(crowdCounting.data.timestamps).pop())
                    setEpoch(Object.values(crowdCounting.data.timestamps).pop())
                }
            }
            // If user window end is very close to the current endLimit, keep it following along
        }

        const id = setInterval(goToLive, TICK_MS);
        if (endLimit > endTime + 60) {
            goToLive();
        }
        return () => clearInterval(id);
    }, [event?.start_time, event?.end_time, crowdCounting?.data, lockedToLive]);

    // useEffect(() => {
    //     if(lockedToLive){
    //         setEndTime(endLimit)
    //     }
    // }, [lockedToLive])

    // // Clamp window any time limits change
    // useEffect(() => {
    //     if (startLimit == null || endLimit == null) return;
    //     if (startTime == null || endTime == null) return;

    //     let s = startTime;
    //     let e = endTime;

    //     if (e < s) [s, e] = [e, s];

    //     s = Math.max(startLimit, s);
    //     e = Math.min(endLimit, e);

    //     // enforce minimum window (1 bucket)
    //     const minRange = Math.max(1, Number(group_size) || 1);
    //     if (e <= s) e = Math.min(endLimit, s + minRange);

    //     if (s !== startTime) setStartTime(s);
    //     if (e !== endTime) setEndTime(e);
    // }, [startLimit, endLimit, startTime, endTime, group_size]);


    const query = {
        start_time: startTime,
        end_time: endTime,
        group_size: Math.ceil((endLimit - startLimit) / 50),
        uuids: device_uuids,
        context_start_time: startLimit
    }


    function onTimelineChange({ start, end }) {
        setStartTime(start);
        setEndTime(end)
    }

    function updateTimestamp(epoch) {
        last_epoch_change = moment().unix();
        // console.log(epoch)
        if (lockedToLive) {
            setEpoch(epoch)
        }

    }


    function a11yProps(index, id_type) {
        return {
            id: `${id_type}-tab-${index}`,
            'aria-controls': `${id_type}-tabpanel-${index}`,
        };
    }



    if (crowdCounting.data?.timestamp_groups[epoch]) {
        current_data = crowdCounting.data?.timestamp_groups[epoch];
    }

    const donut = useMemo(() => {

        if (!crowdCounting.data) {
            return "";
        }

        let chart_settings;

        if (settings.movement == 'flow') {
            if (settings.stacking == 'true') {
                chart_settings = {
                    'data_type': 'flow',
                    'value': settings.direction + '_rate_minute'
                }
            }
            else {
                chart_settings = {
                    'data_type': 'flow',
                    'value': settings.direction + '_rate_minute'
                }
            }
        }
        else {
            if (settings.stacking == 'true') {
                chart_settings = {
                    'data_type': 'accumulative',
                    'value': settings.direction
                }
            }
            else {
                chart_settings = {
                    'data_type': 'accumulative',
                    'value': settings.direction
                }
            }
        }


        return <>
            <DonutChartWrapper {...{ data: current_data, filters: crowdCounting.data.filters, group_type: treeState, value: chart_settings.value, data_type: chart_settings.data_type }} />
        </>

    }, [current_data, settings])


    const chart = useMemo(() => {

        if (!crowdCounting.data) {
            return "";
        }

        let chart_settings;

        // debugger;
        if (settings.movement == 'flow') {
            if (settings.stacking == 'true') {
                chart_settings = {
                    'data_type': 'flow',
                    'stacked': true,
                    'value': settings.direction + '_rate_minute'
                }
            }
            else {
                chart_settings = {
                    'data_type': 'flow',
                    'stacked': false,
                    'value': settings.direction + '_rate_minute'
                }
            }
        }
        else {
            if (settings.stacking == 'true') {
                chart_settings = {
                    'data_type': 'accumulative',
                    'stacked': true,
                    'value': settings.direction
                }
            }
            else {
                chart_settings = {
                    'data_type': 'accumulative',
                    'stacked': false,
                    'value': settings.direction
                }
            }
        }

        let default_data = {
            ...{
                startTime,
                endTime,
                startLimit,
                endLimit,
                timestamps: crowdCounting.data.timestamps,
                datasets: crowdCounting.data.charts.uuid.stacked_accumulative,
                updateTimestamp,
                onTimelineChange,
                lockToEndLimit: lockedToLive && hasLive,
                data: crowdCounting.data.charts, filters: crowdCounting.data.filters, group_type: treeState, highlightEpoch: highlightedTimePeriod?.startTimestamp, highlightDuration: highlightedTimePeriod?.duration
            }
        }


        return <>
            <LineChartWrapper
                {...{
                    ...default_data, ...chart_settings
                }}
            />
        </>

    }, [crowdCounting.data, treeState, settings, lockedToLive, highlightedTimePeriod])

    useEffect(() => {
        if (crowdCounting.data && (!epoch || typeof crowdCounting.data.timestamps[epoch] == 'undefined')) {
            console.log(Object.values(crowdCounting.data.timestamps).pop())
            setEpoch(Object.values(crowdCounting.data.timestamps).pop())
        }
    }, [crowdCounting.data])


    useEffect(() => {

        if (viewTabValue == 2) {
            setSettings(function (current) {
                return {
                    ...current,
                    'breakdown': 'groups',
                    'stacking': 'false',
                    'direction': 'net',
                    'table_binning': 60 * 60,
                    'binning': 'auto'
                }
            })
        }
        else {
            setSettings({
                'breakdown': 'groups',
                'movement': 'accumulative',
                'stacking': 'false',
                'direction': 'net',
                'binning': 'auto',
                'table_binning': 60 * 60,
                'binning': 'auto'
            })

        }


        setTreeState(function (val) {
            val.all.expand = !!viewTabValue;
            return { ...val };
        });

        // debugger;
        if ((viewTabValue == 0 || viewTabValue == 2) && endLimit) { //overview
            setLockedToLive(true)
            setEndTime(endLimit)
            if (crowdCounting.data) {
                // console.log(Object.values(crowdCounting.data.timestamps).pop())
                setEpoch(Object.values(crowdCounting.data.timestamps).pop())
            }
        }


    }, [viewTabValue]);






    function getCumulativeDataForWindows(data, start_time, end_time) {

        try {
            if (!start_time || !end_time || !data.timestamp_groups[start_time] || !data.timestamp_groups[end_time]) {
                return null;
            }

            let period_in_minutes = (end_time - start_time) / 60;


            var collected_data = { flow: { in: 0, out: 0, start: start_time }, accumulative: { ...data.timestamp_groups[start_time].all.accumulative.all } };

            for (var time = start_time; time < end_time; time += data.metadata.group_size) {
                if (typeof data.timestamp_groups[time] != 'undefined') {
                    collected_data.flow.in += data.timestamp_groups[time].all.flow.all.in;
                    collected_data.flow.out += data.timestamp_groups[time].all.flow.all.out;
                }

            }

            collected_data.accumulative.in = data.timestamp_groups[end_time].all.accumulative.all.in - collected_data.accumulative.in;
            collected_data.accumulative.out = data.timestamp_groups[end_time].all.accumulative.all.out - collected_data.accumulative.out;

            collected_data.flow.in_rate_minute = (collected_data.flow.in / period_in_minutes);
            collected_data.flow.out_rate_minute = (collected_data.flow.out / period_in_minutes);
            collected_data.minutes = period_in_minutes;

            return collected_data;
        }
        catch (e) {
            console.error(e)
            debugger;
        }



    }

    // if (crowdCounting.data && epoch) {
    //     getCumulativeDataForWindows(crowdCounting.data, epoch - (60 * 5 * 3), epoch)
    // }

    const goUpOneLevel = () => {
        let path = '..';
        console.log(navigate)
        navigate(path, { relative: "path" });
    }


    const previous_data = useMemo(function () {
        return [5, 15, 30, 60].map(function (minutes) {
            let base_epoch = epoch;
            if (crowdCounting.data?.metadata.group_size) {
                base_epoch -= base_epoch % crowdCounting.data?.metadata.group_size;
            }

            return getCumulativeDataForWindows(crowdCounting.data, base_epoch - (60 * minutes), base_epoch)
        })
    }, [epoch, crowdCounting.data])

    const previous_datas = [];
    // const previous_data = [];

    const value_types = ['net'];
    const groupByOptions = [
        { value: "group", label: "Group" },
        { value: "subgroup", label: "Subgroup" },
        { value: "uuid", label: "Camera" },
    ];

    let columns;

    let time_columns;

    if (settings.movement == 'accumulative') {
        columns = [
            { key: "in", header: "Ingress", width: 110, align: "right", getValue: (r) => r.metrics.in, format: (v) => v.toLocaleString() },
            { key: "out", header: "Egress", width: 110, align: "right", getValue: (r) => r.metrics.out, format: (v) => v.toLocaleString() },
            {
                key: "net", header: "Net", width: 110, align: "right", getValue: (r) => r.metrics.net,
                format: (v) => v.toLocaleString(), color: (v) => (v > 0 ? "success" : v < 0 ? "error" : "dark")
            },
            // add more later:
            // { key:"rate", header:"/min", width: 110, align:"right", getValue:(r)=>r.metrics.rate, format:(v)=>v.toFixed(1) }
        ];

        time_columns = crowdCounting.data?.timestamps.slice(0, 10).map(function (r) {
            return { key: r, header: r, align: "right", getValue: (row) => console.log(row), format: (v) => v };
        });
    }
    else {
        columns = [
            { key: "in", header: "Ingress", width: 110, align: "right", getValue: (r) => r.metrics.in_rate_minute, format: (v) => v.toLocaleString() },
            { key: "out", header: "Egress", width: 110, align: "right", getValue: (r) => r.metrics.out_rate_minute, format: (v) => v.toLocaleString() },
            {
                key: "net", header: "Net", width: 110, align: "right", getValue: (r) => r.metrics.net_rate_minute,
                format: (v) => v.toLocaleString(), color: (v) => (v > 0 ? "success" : v < 0 ? "error" : "dark")
            },
            // add more later:
            // { key:"rate", header:"/min", width: 110, align:"right", getValue:(r)=>r.metrics.rate, format:(v)=>v.toFixed(1) }
        ];

        time_columns = crowdCounting.data?.timestamps.map(function (r) {
            return { key: r, header: r, align: "right", getValue: (row) => console.log(row), format: (v) => v };
        });
    }

    function convertToMetricsByUUID(keys, data) {
        var out = {};

        for (var [uuid, key] of Object.entries(keys)) {
            out[uuid] = data[key];
        }

        return out;
    }


    function convertToMetricsByUUIDwithField(keys, data, field) {
        var out = {};

        for (var [uuid, key] of Object.entries(keys)) {
            out[uuid] = data[key][field];
        }

        return out;
    }


    let metricsByUuid = {};

    let myCorridors = [];
    let allMetricByUuid = [];

    if (current_data && crowdCounting.data) {
        // debugger;
        metricsByUuid = convertToMetricsByUUID(crowdCounting.data.filters.uuid.keys, settings.movement == 'accumulative' ? current_data.uuid.accumulative : current_data.uuid.flow)
        allMetricByUuid = convertToMetricsByUUID(crowdCounting.data.filters.uuid.keys, settings.movement == 'accumulative' ? crowdCounting.data.charts.uuid.accumulative : crowdCounting.data.charts.uuid.flow)
        // debugger;
        let tree_colours = walkKeyTreeColour({}, treeState.all, stationConfig.tree, "all");

        myCorridors = Object.entries(current_data.uuid.flow).map(function ([key, data]) {

            let geojson = stationGeoJSON[key];


            return corridorTemplate(data, geojson, crowdCounting.data.metadata.group_size, key, { in: tree_colours[key], out: tree_colours[key] });
        });

    }

    if (settings.breakdown == 'direction') {
        myCorridors.map(function (row) {
            row.colorA = INGRESS_EGRESS_COLOURS.ingress; //"#4FD1C5", // A → B
            row.colorB = INGRESS_EGRESS_COLOURS.egress; //"#ed636a", // B → A
            return row;
        })
    }

    function formatWithSign(num) {
        if (num == 0) {
            return "0"
        }
        else {
            num = num.toLocaleString();
            if (num[0] != '-') {
                num = '+' + num;
            }
        }

        return num;
    }

    let footer = SETTINGS.filter((row, index) => [0, 2, 3, 4].indexOf(index) == -1).map(function (value, index) {
        return <SoftBox>
            <ControlledRadioButtonsGroup size="small" index={index} {...{ ...value }} value={settings[value.value]} setValue={(new_value) => {
                setSettings(function (row) {
                    row[value.value] = new_value;
                    return { ...row };
                })
            }} />
        </SoftBox>
    })

    let table_function;

    switch (settings.direction) {
        case 'in':
            table_function = (cell) => cell.in;
            break;
        case 'out':
            table_function = (cell) => cell.out * -1;
            break;
        case 'both':
            table_function = (cell) => cell.in - cell.out;
            break;
        case 'net':
            table_function = (cell) => cell.in - cell.out;
            break;
    }

    const objectMap = (obj, fn) => {
        try {
            return Object.fromEntries(
                Object.entries(obj).map(([key, value], index) => {
                    // The callback function (fn) should return the new value
                    return [key, fn(value, key, index)];
                })
            );
        }
        catch (e) {
            return {};
        }

    };

    // debugger;
    let rows = [];

    if (crowdCounting.data && crowdCounting.data.manual_binning[settings.table_binning]) {
        switch (settings.direction) {

        }
        rows = [
            { id: "a", label: "Ingress", colour: INGRESS_EGRESS_COLOURS.ingress, values: objectMap(crowdCounting.data.manual_binning[settings.table_binning].charts.all.flow.all, (r) => r.in) },
            { id: "b", label: "Ingress Cmltv.", colour: INGRESS_EGRESS_COLOURS.ingress, values: objectMap(crowdCounting.data.manual_binning[settings.table_binning].charts.all.accumulative.all, (r) => r.in) },
            { id: "c", label: "Egress", colour: INGRESS_EGRESS_COLOURS.egress, values: objectMap(crowdCounting.data.manual_binning[settings.table_binning].charts.all.flow.all, (r) => r.out) },
            { id: "d", label: "Egress Cmltv.", colour: INGRESS_EGRESS_COLOURS.egress, values: objectMap(crowdCounting.data.manual_binning[settings.table_binning].charts.all.accumulative.all, (r) => r.out) }
        ];
    }

    return (<Box sx={{
        height: { xs: 'auto', md: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f0f2f5',
        overflow: { xs: 'auto', md: 'hidden' }
    }}>

        {/* 1. HEADER (Always Sticky) */}
        <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: '#fff',
            flexShrink: 0,
            boxShadow: '0px 2px 4px rgba(0,0,0,0.05)'

        }}>
            <Box
                sx={{
                    py: 1.5,
                    px: 2,
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    columnGap: 2,
                }}
            >
                <Box sx={{ justifySelf: "start", minWidth: 0 }}>
                    <SoftButton onClick={goUpOneLevel} size="small">
                        <ArrowBack />
                        <Box component="span" sx={{ display: { xxs: "none", xs: "none", md: "none", lg: "inline" } }}>
                            &nbsp;To Event List
                        </Box>
                    </SoftButton>
                </Box>

                <Box textAlign="center" sx={{ minWidth: 0 }}>
                    <SoftTypography variant="h6" fontWeight="bold">
                        {event.name}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="textSecondary">
                        Sydney Olympic Park
                    </SoftTypography>
                </Box>

                <SoftBox
                    sx={{
                        justifySelf: "end",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                    }}
                >
                    {viewTabValue == 1 && hasLive && (
                        <SoftButton
                            sx={{
                                display: { xxs: "none", xs: "none", sm: "none", md: "inline-flex" },
                                padding: 0,
                            }}
                            color={lockedToLive ? "#F00" : "#999"}
                            endIcon={lockedToLive ? <Lock /> : <LockOpen />}
                            onClick={() => setLockedToLive((r) => !r)}
                        >
                            Live
                        </SoftButton>
                    )}

                    <SoftTypography variant="h3" sx={{ display: "inline" }}>
                        {crowdCounting.data &&
                            moment(
                                Math.min(epoch + crowdCounting.data.metadata.group_size, moment().unix()) * 1000
                            ).format("h:mm A")}
                    </SoftTypography>
                </SoftBox>
            </Box>

            <Box sx={{ px: 2, pb: 1 }}>
                <Tabs value={viewTabValue} onChange={(e, v) => setViewTabValue(v)}>
                    <Tab label="Overview" />
                    <Tab label="Breakdown" />
                    <Tab label="Table" />
                </Tabs>
            </Box>
        </Box>

        {/* 2. MAIN CONTENT AREA */}

        <Box component="main" sx={{
            flex: 1,           // Take up all remaining space between header and footer
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // PREVENT THE MAIN BOX FROM EXPANDING OFF SCREEN
            minHeight: 0,       // Allows flex child to shrink below content size
            pb: 1,
            pr: { xs: "8px" },
        }}>

            {viewTabValue == 0 && (
                <Grid container spacing={1} sx={{
                    m: 0,
                    width: '100%',
                    height: "100%",    // Force Grid to stay within the 'main' box
                    flexGrow: 1,
                    overflow: 'hidden'
                }}>
                    {/* Left Card Container */}
                    <Grid item xxs={12} xs={12} md={7} sx={{ height: "100%", display: 'flex' }}>
                        <Card sx={{
                            pt: 2,
                            width: '100%',
                            height: '100%',
                            display: 'flex',          // Card becomes a flex container
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                            overflow: 'hidden'        // Keep the card itself from scrolling
                        }}>
                            {/* THE INTERNAL SCROLL BOX */}
                            <Box sx={{
                                flexGrow: 1,          // Fill the card
                                minHeight: 0,         // Crucial for nested scroll
                                overflowY: { xs: 'auto', md: 'auto' },
                                px: 3,
                                pb: 2
                            }}>
                                {/* Your SoftStatCardXL content here */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <SoftStatCardXL
                                            title={"Net Ingress since " + moment(startLimit * 1000).format("h:mma")}
                                            value={current_data && (current_data.all.accumulative.all.in - current_data.all.accumulative.all.out).toLocaleString()}
                                            unit="net people"

                                            ingress={current_data?.all.accumulative.all.in}
                                            egress={current_data?.all.accumulative.all.out}
                                            // footerRight="33% utilised"
                                            compareTitle="Rolling Windows"
                                            compareLayout="grid" // "row" | "grid" | "stack"
                                            comparisons={
                                                previous_data.map(function (value, key) {
                                                    if (!value) {
                                                        return {
                                                            key, label: "-", prevValue: "-"
                                                        }
                                                    }
                                                    return { key, label: "Last " + value.minutes + " mins", prevValue: formatWithSign(Math.round(value.accumulative.in - value.accumulative.out)) } //delta: 550, deltaSuffix: ""
                                                })
                                            }
                                            // Optional formatter
                                            formatCompareValue={(v) => String(v)}
                                        />

                                    </Grid>
                                    <Grid item xs={12}>
                                        <SoftStatCardXL
                                            title="Flow"
                                            value={current_data && formatWithSign(Math.round(current_data.all.flow.all.in_rate_minute - current_data.all.flow.all.out_rate_minute))}
                                            unit="people / minute"
                                            ingress={current_data?.all.flow.all.in_rate_minute}
                                            egress={current_data?.all.flow.all.out_rate_minute}
                                            compareTitle="Rolling Average"
                                            compareLayout="grid" // "row" | "grid" | "stack"
                                            comparisons={
                                                previous_data.map(function (value, key) {
                                                    if (!value) {
                                                        return {
                                                            key, label: "-", prevValue: "-"
                                                        }
                                                    }
                                                    return { key, label: "Last " + value.minutes + " mins", prevValue: formatWithSign(Math.round(value?.flow.in_rate_minute - value?.flow.out_rate_minute)) } //delta: 550, deltaSuffix: ""
                                                })
                                            }
                                            // Optional formatter
                                            formatCompareValue={(v) => String(v)}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Card>


                    </Grid>

                    {/* Right Card (Map) Container */}
                    <Grid item xxs={12} xs={12} sm={12} md={5} sx={{ height: "100%" }} >
                        <Card sx={{
                            width: '100%',
                            height: '100%',    // Matches the left card exactly
                            position: 'relative',
                            bgcolor: '#e0e0e0',
                            minHeight: { xxs: '200px', md: '100%' },
                            // display: 'flex',
                            // flexDirection: 'column'
                        }}>
                            <Box sx={{ position: 'absolute', inset: 0 }}>
                                {/* Map fills the card completely */}
                                <CorridorDotsMapOptimised sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }} corridors={myCorridors} prefill={true} animate={true} >
                                    <Card sx={{ left: "10px", bottom: "10px", position: 'absolute', bgcolor: '#FFFFFF', display: { xxs: 'none', xs: 'none', sm: 'none', md: 'block' } }}>
                                        <SoftBox m={2}>
                                            <ControlledRadioButtonsGroup  {...{ ...SETTINGS[0] }} value={settings[SETTINGS[0].value]} setValue={(new_value) => {
                                                setSettings(function (row) {
                                                    row[SETTINGS[0].value] = new_value;
                                                    return { ...row };
                                                })
                                            }} />
                                        </SoftBox>
                                    </Card>

                                </CorridorDotsMapOptimised>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>)}

            {viewTabValue == 1 && (
                <Grid container spacing={1} sx={{
                    m: 0,
                    width: '100%',
                    height: "100%",    // Force Grid to stay within the 'main' box
                    flexGrow: 1,
                    overflow: 'hidden'
                }}>
                    {/* Left Card Container */}
                    <Grid item xs={12} md={12} lg={8} xl={5} sx={{ height: "100%", display: 'flex' }}>
                        <SoftUIGroupTable
                            tree={stationConfig.tree}
                            metricsByUuid={metricsByUuid}
                            treeState={treeState}
                            setTreeState={setTreeState}
                            // optional: how to find the root key in treeState
                            rootStateKey="all"
                            columns={columns}
                            onTogglePath={(path) =>
                                setTreeState((prev) => ({
                                    ...prev,
                                    all: toggleExpandAtPath(prev.all, path),
                                }))
                            }
                            sx={{ height: '100%', width: "100%" }}
                        />
                    </Grid>
                    <Grid item xs={12} xl={3} lg={4} md={12} sx={{ height: "100%", display: 'flex' }}>
                        <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',          // Card becomes a flex container
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                            overflow: 'hidden'        // Keep the card itself from scrolling
                        }}>
                            {/* THE INTERNAL SCROLL BOX */}
                            <Box sx={{
                                flexGrow: 1,          // Fill the card
                                minHeight: 0,         // Crucial for nested scroll
                                overflowY: { xs: 'auto', md: 'auto' },
                            }}>
                                <Grid container height="100%">
                                    <Grid item md={12} xl={12} xxs={12} xs={12} sx={{ display: 'flex' }}>
                                        <Card sx={{ width: '100%', height: '100%' }} >
                                            <SoftBox m={2} sx={{ height: '100%' }} >
                                                <SoftTypography variant={"h4"}>
                                                    Distribution
                                                </SoftTypography>
                                                <SoftBox sx={{ width: "100%" }}>
                                                    {donut}
                                                </SoftBox>
                                            </SoftBox>
                                        </Card>
                                    </Grid>
                                    <Grid item md={12} xl={12} sx={{ display: 'flex', width: '100%', paddingTop: '8px' }}>
                                        <Card m={2} sx={{ width: '100%', height: '100%' }}>
                                            <SoftBox m={2}>
                                                <SoftTypography mb={1} variant={"h4"}>
                                                    Filters
                                                </SoftTypography>
                                                <SoftBox>
                                                    <SoftBox mb={1}>
                                                        <Tabs value={settingsTabValue} onChange={(r, value) => { setSettingsTabValue(value) }} aria-label="basic tabs example">
                                                            <Tab label={"Preset"} {...a11yProps(0, 'settings')} />
                                                            <Tab label={"Custom"} {...a11yProps(1, 'settings')} />
                                                        </Tabs>
                                                    </SoftBox>

                                                    <CustomTabPanel value={settingsTabValue} index={0} id_type={'settings'} >
                                                        <Grid container spacing={2}>
                                                            <Grid item xl={6} md={6} xs={12} xxs={12} >
                                                                <SoftUIButtonWithDesc title={"Pre Event"} description={"Main Doors / Net Ingress / Total"} onClick={function () {
                                                                    setSettings((old_value) => {
                                                                        return {
                                                                            ...old_value,
                                                                            'breakdown': 'groups',
                                                                            'movement': 'accumulative',
                                                                            'stacking': 'true',
                                                                            'direction': 'net',
                                                                        }
                                                                    }
                                                                    )

                                                                    setTreeState(function (val) {
                                                                        val.all.expand = true;
                                                                        val.all.children.main.expand = true;
                                                                        val.all.children.main.visible = true;
                                                                        // val.all.children.side.visible = false;
                                                                        return { ...val }
                                                                    })

                                                                }} />
                                                            </Grid>
                                                            <Grid item xl={6} md={6} xs={12} xxs={12}>
                                                                <SoftUIButtonWithDesc title={"Post Event"} description={"Side Doors / Egress / Flow"} onClick={function () {
                                                                    setSettings((old_value) => {
                                                                        return {
                                                                            ...old_value,
                                                                            'breakdown': 'groups',
                                                                            'movement': 'flow',
                                                                            'stacking': 'false',
                                                                            'direction': 'out',
                                                                        }
                                                                    })

                                                                    setTreeState(function (val) {
                                                                        val.all.expand = true;
                                                                        // val.all.children.side.expand = true;
                                                                        // val.all.children.side.visible = true;
                                                                        val.all.children.main.visible = true;
                                                                        return { ...val }
                                                                    })
                                                                }} />
                                                            </Grid>
                                                            <Grid item xl={6} md={6} xs={12} xxs={12}>
                                                                <SoftUIButtonWithDesc title={"Flow Distribution"} description={"All Cameras / Egress + Ingress / Flow"} onClick={function () {
                                                                    setSettings((old_value) => {
                                                                        return {
                                                                            ...old_value,
                                                                            'breakdown': 'groups',
                                                                            'movement': 'flow',
                                                                            'stacking': 'true',
                                                                            'direction': 'both'
                                                                        }
                                                                    })

                                                                    setTreeState(function (val) {
                                                                        val.all.expand = true;
                                                                        // val.all.children.side.visible = true;
                                                                        val.all.children.main.visible = true;
                                                                        // val.all.children.side.expand = true;
                                                                        val.all.children.main.expand = true;
                                                                        return { ...val }
                                                                    })
                                                                }} />
                                                            </Grid>
                                                            <Grid item xl={6} md={6} xs={12} xxs={12}>
                                                                <SoftUIButtonWithDesc title={"Accumulative Distribution"} description={"All Cameras / Egress + Ingress / Total"} onClick={function () {
                                                                    setSettings((old_value) => {
                                                                        return {
                                                                            ...old_value,
                                                                            'breakdown': 'groups',
                                                                            'movement': 'total',
                                                                            'stacking': 'true',
                                                                            'direction': 'both',
                                                                        }
                                                                    })
                                                                    setTreeState(function (val) {
                                                                        val.all.expand = true;
                                                                        // val.all.children.side.visible = true;
                                                                        val.all.children.main.visible = true;
                                                                        // val.all.children.side.expand = true;
                                                                        val.all.children.main.expand = true;
                                                                        return { ...val }
                                                                    })
                                                                }} />
                                                            </Grid>
                                                        </Grid>
                                                    </CustomTabPanel>
                                                    <CustomTabPanel value={settingsTabValue} index={1} id_type={'settings'} >
                                                        <Grid container spacing={2}>
                                                            <Grid item xl={12} md={12} xs={12} xxs={12} >
                                                                <SoftBox>
                                                                    {SETTINGS.filter((row, index) => index && index != 5).map(function (value, index) {
                                                                        return <SoftBox>
                                                                            <ControlledRadioButtonsGroup index={index} {...{ ...value }} value={settings[value.value]} setValue={(new_value) => {
                                                                                setSettings(function (row) {
                                                                                    row[value.value] = new_value;
                                                                                    return { ...row };
                                                                                })
                                                                            }} />
                                                                        </SoftBox>
                                                                    })}
                                                                </SoftBox>

                                                            </Grid>
                                                        </Grid>

                                                    </CustomTabPanel>
                                                </SoftBox>
                                            </SoftBox>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Grid>


                    {/* Right Card (Map) Container */}
                    <Grid item xxs={12} xs={12} sm={12} md={4} sx={{ height: "100%" }} >
                        <Card sx={{
                            width: '100%',
                            height: '100%',    // Matches the left card exactly
                            position: 'relative',
                            bgcolor: '#e0e0e0',
                            minHeight: { xxs: '200px', md: '100%' },
                            // display: 'flex',
                            // flexDirection: 'column'
                        }}>
                            <Box sx={{ position: 'absolute', inset: 0 }}>
                                {/* Map fills the card completely */}
                                <CorridorDotsMapOptimised sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }} corridors={myCorridors} prefill={true} animate={true} >
                                    <Card sx={{ left: "10px", bottom: "10px", position: 'absolute', bgcolor: '#FFFFFF', display: { xxs: 'none', xs: 'none', sm: 'none', md: 'block' } }}>
                                        <SoftBox m={2}>
                                            <ControlledRadioButtonsGroup  {...{ ...SETTINGS[0] }} value={settings[SETTINGS[0].value]} setValue={(new_value) => {
                                                setSettings(function (row) {
                                                    row[SETTINGS[0].value] = new_value;
                                                    return { ...row };
                                                })
                                            }} />
                                        </SoftBox>
                                    </Card>

                                </CorridorDotsMapOptimised>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>)}

            {viewTabValue == 2 && (<Grid container spacing={1} sx={{
                m: 0,
                width: '100%',
                height: "100%",    // Force Grid to stay within the 'main' box
                flexGrow: 1,
                overflow: 'hidden'
            }}>
                {/* Left Card Container */}

                <Grid item xl={12} sx={{ display: 'flex', width: "100%", height: "100%" }}>
                    <SoftUITimeTableShellVerticalFrozen
                        rows={rows}
                        timestamps={crowdCounting.data && crowdCounting.data.manual_binning[settings.table_binning].timestamps}
                        getCellValue={(row, ts) => row.values?.[ts] ?? 0}
                        footerItems={footer}
                        // showTotalColumn={settings.movement != 'accumulative'}
                        sx={{ height: "100%", width: "100%" }}
                        smallValueFontSize="0.95rem"
                        bigValueFontSize="1.75rem"
                        smallRowHeight={35}
                        bigRowHeight={50}
                        onHoverTimePeriod={setHighlightedTimePeriod}
                    // formatTimestamp={(timestamp) => {
                    //     // console.log(timestamp + crowdCounting.data.metadata.group_size)
                    //     let end_time = (Number(timestamp) + crowdCounting.data.metadata.group_size);
                    //     if(end_time > endLimit){
                    //         end_time = endLimit * 1000;
                    //     }
                    //     else{
                    //         end_time = (Number(timestamp) + crowdCounting.data.metadata.group_size) * 1000;
                    //     }

                    //     return moment(timestamp * 1000).format('HH:mm');
                    // }}
                    />
                </Grid>
            </Grid>)}
        </Box >


        {/* 3. FOOTER */}
        {
            (
                <Box sx={{
                    position: { xs: 'static', md: 'sticky' },
                    bottom: 0,
                    height: "max(150px, 20vh)",
                    flexShrink: 0,
                    bgcolor: '#fff',
                    borderTop: '1px solid #ddd',
                    p: 1,
                    zIndex: 1000
                }}>
                    <Box sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {/* <Typography variant="caption">Chart Placeholder (Fixed 200px)</Typography> */}
                        {chart}
                    </Box>
                </Box>
            )
        }
    </Box >)



}
